import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LogOut, User, Sparkles, ShieldCheck, Briefcase, Settings } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/hooks/useAuth";
import { useRoles } from "@/hooks/useRole";
import { supabase } from "@/integrations/supabase/client";
import CompletionBadge from "./CompletionBadge";
import { useProfileCompletion, type ProfileRow } from "@/hooks/useProfileCompletion";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";

const UserAvatarMenu = () => {
  const { user, signOut } = useAuth();
  const { isSuperAdmin, isPartnerAdmin } = useRoles();
  const coachOn = useFeatureFlag("coach");
  const affiliatesOn = useFeatureFlag("affiliates");
  const isOwnerAdmin = isSuperAdmin && user?.email?.toLowerCase() === "emergingmrkt@gmail.com";
  const [displayName, setDisplayName] = useState<string>("");
  const [avatarUrl, setAvatarUrl] = useState<string>("");
  const [profile, setProfile] = useState<ProfileRow | null>(null);

  useEffect(() => {
    if (!user) return;
    let active = true;

    const loadProfile = async () => {
      const { data } = await supabase
        .from("profiles")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();
      if (!active || !data) return;
      setDisplayName(data.display_name ?? "");
      setAvatarUrl(data.avatar_url ?? "");
      setProfile(data as ProfileRow);
    };

    loadProfile();

    // Stay in sync with profile edits made elsewhere (e.g. Settings tab).
    const channel = supabase
      .channel(`profile:${user.id}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "profiles",
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const next = (payload.new ?? {}) as ProfileRow;
          if (typeof next.display_name !== "undefined") setDisplayName(next.display_name ?? "");
          if (typeof next.avatar_url !== "undefined") setAvatarUrl(next.avatar_url ?? "");
          setProfile((prev) => ({ ...(prev ?? {}), ...next }));
        },
      )
      .subscribe();

    return () => {
      active = false;
      supabase.removeChannel(channel);
    };
  }, [user]);

  const { tier } = useProfileCompletion(profile);

  if (!user) return null;

  const initials = (displayName || user.email || "?")
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join("");

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="rounded-full focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background">
        <Avatar className="h-9 w-9 border border-border">
          {avatarUrl ? <AvatarImage src={avatarUrl} alt={displayName || "Profile"} /> : null}
          <AvatarFallback className="bg-muted text-xs font-semibold text-foreground">
            {initials || "U"}
          </AvatarFallback>
        </Avatar>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="space-y-1.5">
          <div className="truncate">{displayName || user.email}</div>
          <CompletionBadge tier={tier} size="sm" />
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/profile" className="cursor-pointer">
            <User className="mr-2 h-4 w-4" />
            My Profile
          </Link>
        </DropdownMenuItem>
        {coachOn && (
          <DropdownMenuItem asChild>
            <Link to="/coach" className="cursor-pointer">
              <Sparkles className="mr-2 h-4 w-4" />
              Assessment
            </Link>
          </DropdownMenuItem>
        )}
        <DropdownMenuItem asChild>
          <Link to="/account" className="cursor-pointer">
            <Settings className="mr-2 h-4 w-4" />
            Account Settings
          </Link>
        </DropdownMenuItem>
        {isOwnerAdmin && (
          <DropdownMenuItem asChild>
            <Link to="/admin" className="cursor-pointer">
              <ShieldCheck className="mr-2 h-4 w-4" />
              Admin Dashboard
            </Link>
          </DropdownMenuItem>
        )}
        {isPartnerAdmin && affiliatesOn && (
          <DropdownMenuItem asChild>
            <Link to="/affiliate/dashboard" className="cursor-pointer">
              <Briefcase className="mr-2 h-4 w-4" />
              Affiliate Dashboard
            </Link>
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={signOut} className="cursor-pointer">
          <LogOut className="mr-2 h-4 w-4" />
          Sign Out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default UserAvatarMenu;
